import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IntegrationServiceId } from '../../common/integrations'
import { ExternalUserServiceIntegration, User } from '../../models/User'
import { RoomSession } from '../../models/Room'
import { Task } from '../../models/MeetingSupport'

export type ModalType =
  | 'createTask'
  | 'exportMeetingSummary'
  | 'updateContact'
  | 'exportDocumentSummary'
  | 'updateDocumentContact'
  | 'saveDocumentSummaryToDocumentConnector'
  | 'saveMeetingSummaryToDocumentConnector'

interface ModalData {
  id: string
  type: ModalType | null
  serviceId: IntegrationServiceId | null
  user?: User
  roomSession?: RoomSession
  tasks?: Task[]
  externalIntegration?: ExternalUserServiceIntegration
}

interface ModalState {
  modals: ModalData[]
}

const initialState: ModalState = {
  modals: [],
}

interface OpenModalPayload {
  type: ModalType
  serviceId: IntegrationServiceId
  user?: User
  roomSession?: RoomSession
  tasks?: Task[]
  externalIntegration?: ExternalUserServiceIntegration
}

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalPayload>) => {
      if (['updateContact', 'updateDocumentContact'].includes(action.payload.type) && !action.payload.user) {
        throw new Error(`User must be provided when opening '${action.payload.type}' modal`)
      }
      if (['exportMeetingSummary', 'createTask'].includes(action.payload.type) && !action.payload.roomSession) {
        throw new Error(`RoomSession must be provided when opening '${action.payload.type}' modal`)
      }
      if (action.payload.type === 'createTask') {
        if (!action.payload.tasks) {
          throw new Error("Tasks must be provided when opening 'createTask' modal")
        }
        if (!action.payload.externalIntegration) {
          throw new Error("ExternalIntegration must be provided when opening 'createTask' modal")
        }
      }

      const modalId = crypto.randomUUID()
      state.modals.push({
        id: modalId,
        ...action.payload,
      })
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter((modal) => modal.id !== action.payload)
    },
    closeAllModals: () => initialState,
  },
})

export const { openModal, closeModal, closeAllModals } = modalSlice.actions
export default modalSlice.reducer
